import React from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Card, Stack, useTheme } from '@mui/material'
import { P, Text } from '@mxenabled/mxui'

import { Drawer, useGlobalCopyStore } from '../../common'
import { buildDynamicCopy } from '../../core/utils/Localization'
interface SnowballDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const SnowballDrawer = ({ isOpen, onClose }: SnowballDrawerProps) => {
  const theme = useTheme()
  const { debts: copy } = useGlobalCopyStore()
  const [readMore, setReadMore] = React.useState(false)

  return (
    <Drawer
      ariaLabelClose={''}
      isOpen={isOpen}
      onClose={onClose}
      title={copy.snowball_drawer_title}
    >
      <Stack bgcolor={theme.palette.background.default} height="100%">
        {/* Drawer Header */}
        <Stack alignItems={'center'} px={24} py={24}>
          <Stack alignItems={'center'} pb={24}>
            <Text mb={12} truncate={true} variant="h3">
              {copy.snowball_drawer_subtitle}
            </Text>
            <Text color={theme.palette.secondary.main} variant="body2">
              {copy.snowball_drawer_amount_description}
            </Text>
            <P variant="h1">{copy.snowball_drawer_savings}</P>
            <Text color={theme.palette.secondary.main} variant="body2">
              {copy.snowball_drawer_duration_description}
            </Text>
          </Stack>
          {/* Drawer Info Card */}
          <Card sx={{ display: 'grid', py: 16, px: 24 }}>
            <Text mb={4} truncate={true} variant="h3">
              {copy.snowball_drawer_question}
            </Text>
            <Text
              color={theme.palette.secondary.main}
              mb={8}
              truncate={!readMore}
              variant="subtitle2"
            >
              {copy.snowball_drawer_answer}
            </Text>
            <Text
              bold={true}
              color={theme.palette.primary.main}
              onClick={() => setReadMore(!readMore)}
              sx={{ cursor: 'pointer' }}
              variant="body2"
            >
              {!readMore ? copy.snowball_drawer_more : copy.snowball_drawer_less}
            </Text>
          </Card>
        </Stack>
        {/* Drawer Payoff Table */}
        <Stack>
          {/* Drawer Disclaimer */}
          <Text
            color={theme.palette.secondary.main}
            px={24}
            sx={{ textAlign: 'center' }}
            variant="body2"
          >
            {copy.snowball_drawer_disclaimer}
          </Text>

          <Stack flexDirection="row" justifyContent="space-between" sx={{ pt: 12, px: 24, pb: 8 }}>
            <Text bold={true} variant="caption">
              {buildDynamicCopy(copy.snowball_drawer_month_header, 1) as string}
            </Text>
            <Text bold={true} variant="caption">
              {copy.snowball_drawer_payment_header}
            </Text>
          </Stack>
          <Stack bgcolor={theme.palette.background.paper} mb={24}>
            <Stack alignItems="center" flexDirection="row" pl={24}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.success.main,
                  marginRight: 12,
                }}
              >
                <Text color="white" fontWeight={700} variant="body2">
                  ✓
                </Text>
              </Box>
              <Stack
                sx={{
                  borderBottom: `1px solid ${theme.palette.neutral.light}`,
                  flexDirection: 'row',
                  flexGrow: 1,
                  py: 14,
                  pr: 24,
                }}
              >
                <Stack flexGrow={1}>
                  <Text variant="body1">
                    {buildDynamicCopy(copy.snowball_drawer_debt_label, 'A') as string}
                  </Text>
                  <Text color={theme.palette.secondary.main} variant="caption">
                    {
                      buildDynamicCopy(
                        copy.snowball_drawer_rollover_explainer_pt1,
                        '$100',
                      ) as string
                    }
                    <strong>
                      {buildDynamicCopy(copy.snowball_drawer_debt_label, 'B') as string}
                    </strong>
                    {copy.snowball_drawer_rollover_explainer_pt2}
                  </Text>
                </Stack>
                <Stack>
                  <Text variant="body1">$100.00</Text>
                  <Text
                    bold={true}
                    color={theme.palette.success.main}
                    textAlign="end"
                    variant="tiny"
                  >
                    {copy.snowball_drawer_paid_label}
                  </Text>
                </Stack>
              </Stack>
            </Stack>
            <Stack alignItems="center" flexDirection="row" pl={24}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  marginRight: 12,
                  borderRadius: '50%',
                  border: `3px solid ${theme.palette.neutral.main}`,
                }}
              />
              <Stack
                sx={{
                  borderBottom: `1px solid ${theme.palette.neutral.light}`,
                  flexDirection: 'row',
                  flexGrow: 1,
                  py: 14,
                  pr: 24,
                }}
              >
                <Text flexGrow={1} variant="body1">
                  {buildDynamicCopy(copy.snowball_drawer_debt_label, 'B') as string}
                </Text>
                <Text variant="body1">$400.00</Text>
              </Stack>
            </Stack>
            <Stack alignItems="center" flexDirection="row" pl={24} py={14}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  marginRight: 12,
                  borderRadius: '50%',
                  border: `3px solid ${theme.palette.neutral.main}`,
                }}
              />
              <Stack flexDirection="row" flexGrow={1} pr={24}>
                <Text flexGrow={1} variant="body1">
                  {buildDynamicCopy(copy.snowball_drawer_debt_label, 'C') as string}
                </Text>
                <Text variant="body1">$1,000.00</Text>
              </Stack>
            </Stack>
            <Stack
              sx={{
                py: 8,
                px: 24,
                flexDirection: 'row',
                justifyContent: 'space-between',
                borderTop: `1px solid ${theme.palette.neutral.light}`,
                borderBottom: `1px solid ${theme.palette.neutral.light}`,
              }}
            >
              <Text bold={true} variant="body1">
                {copy.snowball_drawer_total_label}
              </Text>
              <Text bold={true} variant="body1">
                $1,500.00
              </Text>
            </Stack>
          </Stack>

          <Stack flexDirection="row" justifyContent="space-between" sx={{ pt: 12, px: 24, pb: 8 }}>
            <Text bold={true} variant="caption">
              {buildDynamicCopy(copy.snowball_drawer_month_header, 2) as string}
            </Text>
            <Text bold={true} variant="caption">
              {copy.snowball_drawer_payment_header}
            </Text>
          </Stack>
          <Stack bgcolor={theme.palette.background.paper} mb={24}>
            <Stack alignItems="center" flexDirection="row" pl={24}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.success.main,
                  marginRight: 12,
                }}
              >
                <Text color="white" fontWeight={700} variant="body2">
                  ✓
                </Text>
              </Box>
              <Stack
                sx={{
                  borderBottom: `1px solid ${theme.palette.neutral.light}`,
                  flexDirection: 'row',
                  flexGrow: 1,
                  py: 14,
                  pr: 24,
                }}
              >
                <Stack flexGrow={1}>
                  <Text variant="body1">
                    {buildDynamicCopy(copy.snowball_drawer_debt_label, 'B') as string}
                  </Text>
                  <Text color={theme.palette.secondary.main} variant="caption">
                    {
                      buildDynamicCopy(
                        copy.snowball_drawer_rollover_explainer_pt1,
                        '$500',
                      ) as string
                    }
                    <strong>
                      {buildDynamicCopy(copy.snowball_drawer_debt_label, 'C') as string}
                    </strong>
                    {copy.snowball_drawer_rollover_explainer_pt2}
                  </Text>
                </Stack>
                <Stack>
                  <Text variant="body1">$500.00</Text>
                  <Text
                    bold={true}
                    color={theme.palette.success.main}
                    textAlign="end"
                    variant="tiny"
                  >
                    {copy.snowball_drawer_paid_label}
                  </Text>
                </Stack>
              </Stack>
            </Stack>
            <Stack alignItems="center" flexDirection="row" pl={24}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  marginRight: 12,
                  borderRadius: '50%',
                  border: `3px solid ${theme.palette.neutral.main}`,
                }}
              />
              <Stack
                sx={{
                  flexDirection: 'row',
                  flexGrow: 1,
                  py: 14,
                  pr: 24,
                }}
              >
                <Text flexGrow={1} variant="body1">
                  {buildDynamicCopy(copy.snowball_drawer_debt_label, 'C') as string}
                </Text>
                <Text variant="body1">$1,000.00</Text>
              </Stack>
            </Stack>
            <Stack
              sx={{
                py: 8,
                px: 24,
                flexDirection: 'row',
                justifyContent: 'space-between',
                borderTop: `1px solid ${theme.palette.neutral.light}`,
                borderBottom: `1px solid ${theme.palette.neutral.light}`,
              }}
            >
              <Text bold={true} variant="body1">
                {copy.snowball_drawer_total_label}
              </Text>
              <Text bold={true} variant="body1">
                $1,500.00
              </Text>
            </Stack>
          </Stack>

          <Stack flexDirection="row" justifyContent="space-between" sx={{ pt: 12, px: 24, pb: 8 }}>
            <Text bold={true} variant="caption">
              {buildDynamicCopy(copy.snowball_drawer_month_header, 3) as string}
            </Text>
            <Text bold={true} variant="caption">
              {copy.snowball_drawer_payment_header}
            </Text>
          </Stack>
          <Stack bgcolor={theme.palette.background.paper} mb={24}>
            <Stack alignItems="center" flexDirection="row" pl={24}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.success.main,
                  marginRight: 12,
                }}
              >
                <Text color="white" fontWeight={700} variant="body2">
                  ✓
                </Text>
              </Box>
              <Stack
                sx={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  flexGrow: 1,
                  py: 14,
                  pr: 24,
                }}
              >
                <Stack flexGrow={1}>
                  <Text variant="body1">
                    {buildDynamicCopy(copy.snowball_drawer_debt_label, 'C') as string}
                  </Text>
                </Stack>
                <Stack>
                  <Text variant="body1">$1,500.00</Text>
                  <Text
                    bold={true}
                    color={theme.palette.success.main}
                    textAlign="end"
                    variant="tiny"
                  >
                    {copy.snowball_drawer_paid_label}
                  </Text>
                </Stack>
              </Stack>
            </Stack>
            <Stack
              sx={{
                py: 8,
                px: 24,
                flexDirection: 'row',
                justifyContent: 'space-between',
                borderTop: `1px solid ${theme.palette.neutral.light}`,
                borderBottom: `1px solid ${theme.palette.neutral.light}`,
              }}
            >
              <Text bold={true} variant="body1">
                {copy.snowball_drawer_total_label}
              </Text>
              <Text bold={true} variant="body1">
                $1,500.00
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default observer(SnowballDrawer)
